import * as StompJs from '@stomp/stompjs';
import { computed, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { v4 as uuidgen } from 'uuid';
import { VmUtils } from '.';
import * as Api from './sdk';
import { getStreamGrantStore } from './StreamGrantStore';
import { UserSessionContext } from './viewModels/index';

export interface IRemoteEventSubscriptionContext<T = any> {
	callback: (event: Api.IRemoteEvent<T> | Api.IRemoteEvent<T>[]) => void;
	deliveryOptions?: Api.IRemoteEventsDeliveryOptions;
	id: string;
	nextCallbackTimeoutHandle?: any;
	route: string;
	stompSubscription?: StompJs.StompSubscription;
	valueCache?: { type: string; value: T }[];
}

const instanceId = uuidgen().substring(0, 8);

export class RemoteEventsViewModel<TUserSession extends UserSessionContext = UserSessionContext> {
	private mConnectionPromise: Promise<void>;
	public eventLogger: Api.IEventLoggingService;

	private mStompClient: StompJs.Client;
	private mSubscriptions: IRemoteEventSubscriptionContext[];
	private mUserSession: TUserSession;
	private connectionUuid: string;
	private buildHash: string;
	private static DefaultReconnectDelay = 5000;
	private static LoggingCategory = 'RemoteEvents';
	// The reconnect loop re-fires the same error for as long as the socket stays unreachable, and a tab holds several
	// instances, so report each error name once per tab until a connect succeeds.
	private static loggedErrorsSinceConnect = new Set<string>();

	constructor(userSession: TUserSession, eventLogger?: Api.IEventLoggingService) {
		makeObservable<RemoteEventsViewModel, 'mConnectionPromise'>(this, {
			mConnectionPromise: observable.ref,
			connectionStatus: computed,
		});

		this.eventLogger = eventLogger;
		this.mSubscriptions = [];
		this.mUserSession = userSession;
		this.buildHash = __BUILD_HASH__;
		this.connectionUuid = uuidgen().substring(0, 8);
	}

	public get userSession() {
		return this.mUserSession;
	}

	public get connectionStatus() {
		if (!this.mStompClient) return Api.RemoteEventsConnectionStatus.Disconnected;

		return this.mStompClient.connected
			? Api.RemoteEventsConnectionStatus.Connected
			: Api.RemoteEventsConnectionStatus.Disconnected;
	}

	public connect = (stompConfig?: StompJs.StompConfig) => {
		if (!this.mConnectionPromise) {
			this.mConnectionPromise = new Promise<void>(resolveOnConnect => {
				const client = new StompJs.Client();
				this.mStompClient = client;
				// StompJs neither retries nor recovers when this hook rejects or never settles, so a missing passcode
				// (a stream grant that failed to mint) is retried here on the client's own reconnect cadence.
				client.beforeConnect = async () => {
					while (client.active && this.mStompClient === client) {
						const config = await this.getConnectConfig(stompConfig, resolveOnConnect);
						if (config?.connectHeaders?.passcode) {
							client.configure(config);
							return;
						}
						this.logWebSocketError('AttemptedConnectWithoutPasscode');
						await new Promise(resolve =>
							setTimeout(resolve, client.reconnectDelay || RemoteEventsViewModel.DefaultReconnectDelay)
						);
					}
				};
				client.activate();
			});
		}
		return this.mConnectionPromise;
	};

	/** Note: After calling this, you cannot successfully re-connect. Create a new RemoteEventsViewModel if needed. */
	public permanentlyDisconnect = async () => {
		if (this.mStompClient) {
			try {
				this.mSubscriptions?.forEach(x => {
					x.stompSubscription?.unsubscribe();
					if (x.nextCallbackTimeoutHandle) {
						clearTimeout(x.nextCallbackTimeoutHandle);
						x.nextCallbackTimeoutHandle = null;
					}

					x.valueCache = null;
				});
				await this.mStompClient.deactivate();
			} catch {
				//
			}
			this.mSubscriptions = [];

			this.mStompClient = null;
		}
	};

	public addEventHandler = <T = any>(
		route: string,
		callback: (e: Api.IRemoteEvent<T> | Api.IRemoteEvent<T>[]) => void,
		deliveryOptions?: Api.IRemoteEventsDeliveryOptions
	) => {
		const handlerId = uuidgen();
		const context: IRemoteEventSubscriptionContext<T> = {
			callback,
			deliveryOptions,
			id: handlerId,
			route,
		};
		context.stompSubscription = this.subscribe(context);
		this.mSubscriptions.push(context);

		let disposed = false;
		return {
			dispose: () => {
				if (!disposed) {
					context.stompSubscription?.unsubscribe();
					const index = this.mSubscriptions.indexOf(context);
					if (index >= 0) {
						this.mSubscriptions.splice(index, 1);
					}
					disposed = true;
				}
			},
			id: handlerId,
		};
	};

	private getAccessToken = async () => {
		const credential = await this.mUserSession.webServiceHelper.getCredentialStore().getCredential();
		if (
			!!credential?.access_token &&
			!!credential?.account_id &&
			credential.account_id === this.mUserSession.account?.id &&
			!!credential?.expires_utc &&
			moment(credential.expires_utc).isAfter(moment())
		) {
			return credential.access_token;
		}
		return null;
	};

	private getConnectConfig = async (
		stompConfig: StompJs.StompConfig | undefined,
		resolveOnConnect: () => void
	): Promise<StompJs.StompConfig | null> => {
		let baseConfig: StompJs.StompConfig;
		try {
			baseConfig = await this.configure();
		} catch {
			return null;
		}

		return {
			...baseConfig,
			...(stompConfig || {}),
			onConnect: () => {
				this.onConnect();

				this.mConnectionPromise = null;
				resolveOnConnect();
			},
		};
	};

	private getConnectHeaders = async () => {
		// A tab on a stored token (an admin's "log in as", managed-account access) sends it with every request,
		// so the grant endpoint sees a bearer caller and refuses; the broker accepts the token itself.
		const accessToken = await this.getAccessToken();
		if (!this.mUserSession.webServiceHelper.cookieSession || accessToken) {
			return {
				login: this.mUserSession.user.id,
				passcode: accessToken,
			};
		}

		// The broker resolves the user from the grant and checks the login header against it, so the
		// header has to be the user the grant was minted for rather than the one the session believes in.
		const grant = await getStreamGrantStore(this.mUserSession).get();
		return {
			login: grant?.userId,
			passcode: grant?.grant,
		};
	};

	private configure = async () => {
		if (!this.mUserSession.user?.id) {
			return undefined;
		}
		const config: StompJs.StompConfig = {
			brokerURL: import.meta.env.VITE_WS_URL,
			connectionTimeout: 30000,
			connectHeaders: await this.getConnectHeaders(),
			onDisconnect: VmUtils.Noop,
			onStompError: this.logWebSocketError('onStompError'),
			onUnhandledFrame: this.logWebSocketError('onUnhandledFrame'),
			onUnhandledMessage: this.logWebSocketError('onUnhandledMessage'),
			onUnhandledReceipt: this.logWebSocketError('onUnhandledReceipt'),
			onWebSocketClose: VmUtils.Noop,
			onWebSocketError: this.logWebSocketError('onWebSocketError'),
			reconnectDelay: RemoteEventsViewModel.DefaultReconnectDelay,
		};
		return config;
	};

	private logWebSocketError = (name: string) => (e?: Event | StompJs.IFrame | CloseEvent) => {
		if (!!e && !!this.eventLogger) {
			if (RemoteEventsViewModel.loggedErrorsSinceConnect.has(name)) {
				return;
			}
			RemoteEventsViewModel.loggedErrorsSinceConnect.add(name);
			this.eventLogger.logEvent(
				{
					action: `Websocket-${name}-Error`,
					category: RemoteEventsViewModel.LoggingCategory,
				},
				{ event: e }
			);
		}
	};

	private subscribe = <T = any>(context: IRemoteEventSubscriptionContext<T>) => {
		// The session can be torn down (logout) while a connect is still in flight, which makes the check configure()
		// ran before the socket opened stale by the time we get here.
		const userId = this.mUserSession.user?.id;
		const accountId = this.mUserSession.account?.id;
		if (!userId || !accountId || !this.mStompClient) {
			return undefined;
		}

		// Order is important for the part components, the authorization check in the API expects the following structure:
		// The queue name MUST start with stomp
		// The significant parts must be separated by an _
		// The next part following stomp_ MUST be the user id.

		const queueSuffix = `${userId}_${this.buildHash}_${instanceId}_${this.connectionUuid}`;
		return this.mStompClient.subscribe(`/topic/Account.${accountId}.${context.route}`, this.onMessage(context), {
			id: context.id,
			'x-queue-name': `stomp_${queueSuffix}`,
		});
	};

	private onConnect = () => {
		RemoteEventsViewModel.loggedErrorsSinceConnect.clear();
		// reconnect active subscriptions
		this.mSubscriptions.forEach(x => {
			x.stompSubscription = this.subscribe(x);
		});
	};

	protected onMessage =
		<T = any>(context: IRemoteEventSubscriptionContext<T>) =>
		(message: StompJs.IFrame) => {
			if (!!message?.body && !!message.headers?.type) {
				const composeEvent = (e: { type: string; value: T }) => {
					const event: Api.IRemoteEvent<T> = {
						id: uuidgen(),
						target: {
							handlerId: context.id,
						},
						value: e.value,
						valueType: e.type,
					};
					return event;
				};

				if (context.deliveryOptions?.delay > 0) {
					const valueCache = context.deliveryOptions?.type === 'allSinceLastDelivery' ? context.valueCache || [] : [];
					valueCache.push({
						type: message.headers.type,
						value: JSON.parse(message.body),
					});
					context.valueCache = valueCache;

					if (!context.nextCallbackTimeoutHandle) {
						context.nextCallbackTimeoutHandle = setTimeout(() => {
							if (context.valueCache?.length > 0) {
								context.callback((context.valueCache || []).map(composeEvent));
							}

							context.valueCache = null;
							context.nextCallbackTimeoutHandle = null;
						}, context.deliveryOptions.delay);
					}
					return;
				}

				context.callback(
					composeEvent({
						type: message.headers.type,
						value: JSON.parse(message.body),
					})
				);
			}
		};
}
